"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.destinationSchema = exports.attemptSchema = exports.attemptInputSchema = exports.OUTCOMES_BY_CHANNEL = exports.attemptChannel = void 0;
exports.endsItsStep = endsItsStep;
// A notification attempt, and the destinations a workup offers for one (`RPM-026`, `J-221`), and the
// attempt log each one is a line of (`RPM-040`, `J-229`).
//
// `RPM-026`'s API row: `POST /api/rpm/v1/alerts/:id/attempts`, with the destination "in the body,
// never in the path or the query string". The destination is recorded verbatim as used, because the
// verbatim value is the audit record.
//
// THE FOUR OUTCOMES ARE `RPM-042`'s (`J-230`): orthogonal, no compound value. `REACHED`, `TEXTED` and
// `EMAILED` are terminal for their channel; `NO_ANSWER` is terminal for nothing. The contract carries
// the whole enum, projected from the database's, so an unknown value is `400` before anything is
// read. A known outcome that does not belong to its channel is well formed and still meaningless, so
// the attempt route refuses it `422` against `OUTCOMES_BY_CHANNEL` (`J-212`'s split), and the
// database's `na_channel_outcome` is the second defence.
const zod_1 = require("zod");
const rpm_enums_js_1 = require("./db/rpm-enums.js");
/**
 * The three channels a nurse logs by hand. `EMR_MESSAGE` is a channel the enum carries and no
 * requirement built so far lets her choose.
 */
exports.attemptChannel = zod_1.z.enum(["PHONE", "TEXT", "EMAIL"]);
/**
 * The outcomes each hand-logged channel admits: `na_channel_outcome`'s rows for those channels. A call
 * reaches someone or goes unanswered; a text is texted and an email emailed.
 */
exports.OUTCOMES_BY_CHANNEL = {
    PHONE: ["REACHED", "NO_ANSWER"],
    TEXT: ["TEXTED"],
    EMAIL: ["EMAILED"],
};
/**
 * Whether an outcome ends the step it is logged against (`RPM-042`): `REACHED`, `TEXTED` and `EMAILED`
 * are terminal for their channel, and `NO_ANSWER` for nothing. The set is the batch's own terminal
 * vocabulary, `batch_terminal_outcome`, which is why a batch can never close as `NO_ANSWER` (`CR-10`).
 */
function endsItsStep(outcome) {
    return rpm_enums_js_1.batchTerminal.enumValues.includes(outcome);
}
exports.attemptInputSchema = zod_1.z
    .strictObject({
    channel: exports.attemptChannel,
    /** As she used it, untrimmed: the verbatim value is the record. */
    destinationValue: zod_1.z
        .string()
        .max(320)
        .refine((v) => v.trim() !== "", { message: "Say where the attempt went." }),
    /**
     * What happened, one of the four (`RPM-042`). A call must say; an email is `EMAILED` and a text
     * `TEXTED`, so either may leave it out, and neither may name another channel's outcome.
     */
    outcome: zod_1.z.enum(rpm_enums_js_1.attemptOutcome.enumValues).optional(),
    /**
     * The routing step this attempt performs: the latest resolution and the step's number (`RPM-070`,
     * `J-284`). Both or neither: an attempt to a person she added names no step.
     */
    routingResolutionId: zod_1.z.uuid().optional(),
    routingStep: zod_1.z.number().int().min(1).optional(),
    /**
     * Who was actually spoken to, as she records it (`notification_attempts.contact_person_text`,
     * `RPM-040`). Free text: the quick-fill roles append ` — <role>` to what she typed.
     */
    contactPersonText: zod_1.z.string().max(200).optional(),
    /**
     * The batch this call was made for (`RPM-040`'s Blocking row). An attempt on an entry whose call
     * is held for a batch is refused unless it names that batch; naming it writes one row per resident
     * the batch covers.
     */
    heldCallId: zod_1.z.uuid().optional(),
})
    // An outcome that does not belong to its channel is refused `422` by the route, not here: see above.
    .superRefine((input, ctx) => {
    if (input.channel === "PHONE" && input.outcome === undefined)
        ctx.addIssue({
            code: "custom",
            path: ["outcome"],
            message: "Say whether the call reached someone.",
        });
    if ((input.routingResolutionId === undefined) !== (input.routingStep === undefined))
        ctx.addIssue({
            code: "custom",
            path: ["routingStep"],
            message: "Name the routing resolution and its step together, or neither.",
        });
    if (input.heldCallId !== undefined && input.channel !== "PHONE")
        ctx.addIssue({
            code: "custom",
            path: ["heldCallId"],
            message: "A held call is a phone call: only a call is logged on a batch.",
        });
})
    .meta({ description: "One attempt to notify: the channel, where it went, and a call's outcome" });
/**
 * One line of the attempt log (`RPM-040`): one row per thing that happened, numbered contiguously per
 * alert, with the channel, the destination as used, the outcome, who logged it and when.
 */
exports.attemptSchema = zod_1.z
    .object({
    notificationAttemptId: zod_1.z.number().int(),
    sequenceNo: zod_1.z.number().int(),
    channel: zod_1.z.enum(rpm_enums_js_1.notifyChannel.enumValues),
    outcome: zod_1.z.enum(rpm_enums_js_1.attemptOutcome.enumValues),
    destinationKind: zod_1.z.enum(rpm_enums_js_1.destinationKind.enumValues),
    destinationValue: zod_1.z.string(),
    /** What she was told the destination was: the routed recipient's label, or the added person. */
    destinationLabel: zod_1.z.string().nullable(),
    /** Who was actually spoken to, as she recorded it. */
    contactPersonText: zod_1.z.string().nullable(),
    attemptedAt: zod_1.z.iso.datetime(),
    /** The account that logged it, with its name as the account holds it. */
    attemptedBy: zod_1.z.object({ userId: zod_1.z.string(), name: zod_1.z.string().nullable() }),
    /** The routing step it was logged against; null for an attempt to a person she added. */
    routingStep: zod_1.z.number().int().nullable(),
    /** The batch it was made for; null for an attempt made outside a batch. */
    heldCallId: zod_1.z.uuid().nullable(),
    /**
     * Whether the outcome ends the step it was logged against (`RPM-042`): true for `REACHED`,
     * `TEXTED` and `EMAILED`; false for `NO_ANSWER`, which leaves the step owed.
     */
    endsStep: zod_1.z.boolean(),
    /**
     * The past-tense chart sentence this attempt makes true, offered for pasting only once it is
     * logged (`RPM-042` rows 6 to 10). Derived from the row, never stored.
     */
    chartSentence: zod_1.z.string(),
})
    .meta({ id: "NotificationAttempt", description: "One attempt to notify, as recorded" });
/**
 * One value the nurse typed on this alert that an attempt could go to (`RPM-026`, `RPM-099`): each
 * person's email and phone, as typed. A phone entry carrying do-not-call wording is not offered for
 * a call, and says why.
 */
exports.destinationSchema = zod_1.z
    .object({
    personName: zod_1.z.string(),
    /** The field it was typed in. */
    field: zod_1.z.enum(["EMAIL", "PHONE"]),
    /** As typed. */
    value: zod_1.z.string(),
    /** For a phone entry, the dialable run `parsePhone` found; null where it found none. */
    dial: zod_1.z.string().nullable(),
    /** False for a phone entry that says not to call. */
    callable: zod_1.z.boolean(),
    reason: zod_1.z.string().nullable(),
})
    .meta({ id: "Destination", description: "A destination typed on this alert" });
//# sourceMappingURL=rpm-attempts.js.map